import { PITCH_XI_ROWS, PITCH_BENCH_ROW } from './player-row';
import PitchShell from './pitch-shell';
import PitchPlayer, { PlayerInfo } from './pitch-player';
import { PlayerCircle } from './player-circle';
import { buildXiRowsByPosition } from './team-helpers';
import type { TeamMember } from './types';

type ReadOnlyPitchProps = {
  /** 15 slots, XI-first (0-10) then bench (11-14). */
  team: TeamMember[];
  /** Player id (UUID) of the captain, to show the "C" armband. */
  captainId?: string | null;
};

const EmptySlot = () => (
  <div className="flex justify-center z-10">
    <div className="flex flex-col items-center">
      <PlayerCircle />
      <PlayerInfo invisible />
    </div>
  </div>
);

const renderMember = (
  { playerId, player }: TeamMember,
  captainId?: string | null
) =>
  player ? (
    <PitchPlayer
      key={`ro-${playerId}`}
      name={player.displayName}
      teamCode={player.team.shortName}
      price={`£${player.price.toFixed(1)}m`}
      imageUrl={player.imageUrl}
      isCaptain={Boolean(captainId) && player.id === captainId}
    />
  ) : (
    <EmptySlot key={`ro-${playerId}`} />
  );

const ReadOnlyRow = ({
  members,
  className,
  captainId
}: {
  members: TeamMember[];
  className: string;
  captainId?: string | null;
}) => (
  <div className={`flex items-end ${className}`}>
    {members.map((member) => renderMember(member, captainId))}
  </div>
);

const membersInRange = (team: TeamMember[], range: [number, number]) =>
  team.filter(({ playerId }) => playerId >= range[0] && playerId <= range[1]);

/**
 * Non-interactive version of the builder pitch used on the rating reveal
 * (spec 5.3): identical styling and player tokens, but no slot picker or
 * remove control, and the captain carries a "C" armband instead.
 *
 * The rated squad can come back in any legal formation, so the starting XI is
 * grouped by each player's actual position rather than the builder's fixed
 * rows; the fixed rows are the fallback when the XI isn't fully resolved.
 */
const ReadOnlyPitch = ({ team, captainId }: ReadOnlyPitchProps) => {
  const positionRows = buildXiRowsByPosition(team);
  const xiRows: Array<{
    key: string;
    members: TeamMember[];
    className: string;
  }> =
    positionRows ??
    PITCH_XI_ROWS.map((row) => ({
      key: `pos-${row.positionId}`,
      members: membersInRange(team, row.range),
      className: row.className
    }));

  return (
    <PitchShell
      xi={xiRows.map((row) => (
        <ReadOnlyRow
          key={`ro-row-${row.key}`}
          members={row.members}
          className={row.className}
          captainId={captainId}
        />
      ))}
      bench={
        <ReadOnlyRow
          members={membersInRange(team, PITCH_BENCH_ROW.range)}
          className={PITCH_BENCH_ROW.className}
          captainId={captainId}
        />
      }
    />
  );
};

export default ReadOnlyPitch;
